import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type HasMany,
} from '@ember-data/model';
import type { Type } from '@warp-drive/core-types/symbols';
import { DateTime } from 'scaleflow/utils/date-time';
import type InitiativeModel from './initiative';
import type TfoModel from './tfo';

// An initiative's duration mirrors the number of approach plan weeks, so it is
// recalculated from the full week list rather than nudged up/down by one. A
// dropped request, a week created or removed elsewhere, or a stale stored value
// can't make the count drift that way — after every create/delete the duration
// is whatever the weeks actually add up to.
async function syncDurationToWeekCount(initiative: InitiativeModel) {
  const weeks = await initiative.weeks;
  initiative.duration = String(weeks?.length ?? 0);
  await initiative.save();
}

export default class WeekModel extends Model {
  declare [Type]: 'week';
  @belongsTo('initiative', { inverse: 'weeks', async: true })
  declare initiative: AsyncBelongsTo<InitiativeModel>;
  @attr('string') declare title: string;
  @attr declare text_delta: unknown;
  @attr('string') declare text: string;
  @attr('number') declare position: number;
  @attr('string') declare ISOWeek: string;
  @hasMany('tfo', {
    async: false,
    inverse: 'week',
  })
  declare tfos: HasMany<TfoModel>;
  @attr('boolean') declare archived: boolean;
  @attr('boolean') declare weekOpen: boolean;
  @attr declare tags: unknown;
  @attr declare mentions: unknown;

  get isCurrentWeek() {
    const startDate = this.initiative.content?.startDate;
    if (!startDate || !this.position) return false;
    const weekStart = DateTime.from(startDate)
      .add(this.position - 1, 'weeks')
      .startOf('day');
    const today = DateTime.now().startOf('day');
    return (
      today.isSameOrAfter(weekStart) && today.isBefore(weekStart.add(7, 'days'))
    );
  }

  async save() {
    const wasNew = this.isNew;
    const initiative = wasNew ? await this.initiative : null;
    const result = await super.save();
    if (wasNew && initiative) {
      await syncDurationToWeekCount(initiative);
    }
    return result;
  }

  async destroyRecord(options?: Parameters<Model['destroyRecord']>[0]) {
    // A second delete while the first is still committing used to put a second
    // DELETE on the wire. The winner's 204 clears this record's cache entry,
    // then the loser's 404 is handed to WarpDrive's commitWasRejected, which
    // reads `inflightAttrs` off the entry that is no longer there. Bailing here
    // means the second request is never made.
    if (
      this.isDestroyed ||
      this.isDestroying ||
      this.isSaving ||
      this.isDeleted
    )
      return this;

    // Read the initiative synchronously: awaiting the relationship here delayed
    // the DELETE by however long the fetch took, and two clicks arriving during
    // that window both resumed on the same resolved promise and fired a DELETE
    // in the same tick. Deleting a week only happens from inside the
    // initiative, so it is already loaded. Capture it before the delete, since
    // a successful delete unloads the record and takes the relationship with it.
    const initiative = this.initiative?.content ?? null;
    const result = await super.destroyRecord(options);
    if (initiative) {
      await syncDurationToWeekCount(initiative);
    }
    return result;
  }
}
